import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

// Featured Article Card - Large format for homepage hero
export const ArticleCardFeatured = ({ post, className }) => {
  const formattedDate = new Date(post.published_at || post.created_at || post.date).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const categories = Array.isArray(post.category) ? post.category : [post.category];

  return (
    <article className={cn("group relative", className)}>
      <Link to={`/yazi/${post.slug}`} className="block">
        <div className="relative bg-card border border-border rounded-sm overflow-hidden card-hover">
          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Category */}
            <div className="flex items-center gap-2 mb-4">
              {categories.slice(0, 2).map((cat) => (
                <span key={cat} className="category-badge">
                  {cat}
                </span>
              ))}
            </div>

            {/* Title */}
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground leading-snug mb-3 group-hover:text-accent transition-colors">
              {post.title}
            </h3>

            {/* Subtitle */}
            {post.subtitle && (
              <p className="text-muted-foreground text-base mb-4 line-clamp-2">
                {post.subtitle}
              </p>
            )}

            {/* Excerpt */}
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 line-clamp-3">
              {post.excerpt}
            </p>

            {/* Meta */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>{formattedDate}</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {post.reading_time || post.readingTime} dk
                </span>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-accent group-hover:gap-2 transition-all">
                Oku
                <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>

          {/* Decorative Element */}
          <div className="absolute top-0 right-0 w-24 h-24 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-foreground">
              <path 
                d="M20 50 L80 50 M75 45 L80 50 L75 55" 
                stroke="currentColor" 
                strokeWidth="2" 
                fill="none"
              />
              <circle cx="20" cy="50" r="5" fill="currentColor" />
            </svg>
          </div>
        </div>
      </Link>
    </article>
  );
};

// Standard Article Card - For listings
export const ArticleCard = ({ post, className, variant = 'default' }) => {
  const formattedDate = new Date(post.published_at || post.created_at || post.date).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long'
  });

  const categories = Array.isArray(post.category) ? post.category : [post.category];

  if (variant === 'compact') {
    return (
      <article className={cn("group", className)}>
        <Link to={`/yazi/${post.slug}`} className="flex gap-4 p-4 -mx-4 rounded-sm hover:bg-muted/50 transition-colors">
          {/* Number/Order */}
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-lg font-serif font-bold text-muted-foreground/50">
            {'→'}
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-serif text-base font-bold text-foreground leading-snug mb-1 group-hover:text-accent transition-colors line-clamp-2">
              {post.title}
            </h4>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{formattedDate}</span>
              <span>{post.reading_time || post.readingTime} dk okuma</span>
            </div>
          </div>
          
          <ArrowRight className="flex-shrink-0 h-4 w-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all mt-1" />
        </Link>
      </article>
    );
  }

  return (
    <article className={cn("group", className)}>
      <Link to={`/yazi/${post.slug}`} className="block">
        <div className="border-b border-border pb-6 group-hover:border-accent/30 transition-colors">
          {/* Category */}
          <div className="flex items-center gap-2 mb-3">
            {categories.slice(0, 1).map((cat) => (
              <span key={cat} className="text-xs font-medium uppercase tracking-wider text-accent">
                {cat}
              </span>
            ))}
          </div>

          {/* Title */}
          <h3 className="font-serif text-xl font-bold text-foreground leading-snug mb-2 group-hover:text-accent transition-colors">
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-2">
            {post.excerpt}
          </p>

          {/* Meta */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{formattedDate}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.reading_time || post.readingTime} dk
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
};

// Horizontal Article Card - For related posts
export const ArticleCardHorizontal = ({ post, className }) => {
  const formattedDate = new Date(post.published_at || post.created_at || post.date).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short'
  });

  const categories = Array.isArray(post.category) ? post.category : [post.category];

  return (
    <article className={cn("group", className)}>
      <Link to={`/yazi/${post.slug}`} className="flex flex-col sm:flex-row gap-4">
        {/* Placeholder for potential image */}
        <div className="flex-shrink-0 w-full sm:w-32 h-24 bg-muted rounded-sm flex items-center justify-center">
          <svg viewBox="0 0 60 40" className="w-12 h-8 text-muted-foreground/30">
            <rect x="5" y="5" width="50" height="30" fill="none" stroke="currentColor" strokeWidth="1" />
            <line x1="30" y1="5" x2="30" y2="35" stroke="currentColor" strokeWidth="1" />
            <circle cx="30" cy="20" r="5" fill="none" stroke="currentColor" strokeWidth="1" />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          {/* Category */}
          <span className="text-xs font-medium uppercase tracking-wider text-accent">
            {categories[0]}
          </span>

          {/* Title */}
          <h4 className="font-serif text-base font-bold text-foreground leading-snug mt-1 mb-2 group-hover:text-accent transition-colors line-clamp-2">
            {post.title}
          </h4>

          {/* Meta */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{formattedDate}</span>
            <span>{post.reading_time || post.readingTime} dk</span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default ArticleCard;
